import classNames from 'classnames';
import {Provider} from 'mobx-react';
import React, {useEffect} from 'react';

import {BrowseCourse} from '@udemy/browse-course';
import {Tracker} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CertificationOccupation,
    CertificationTopicHeader,
    CertificationTopicStatsSection,
    CertificationVoucherBundleUnitCarousel,
} from '@udemy/react-certification-preparation';
import {useExamByTopicId} from '@udemy/react-certification-vouchers';
import {QuestionsAndAnswers} from '@udemy/react-frequently-asked-questions';
import {ContentArea, ContentGrid, ContentGridItem} from '@udemy/react-layout-components';
import {CONTEXT_TYPES, ResourceContextMenuProvider} from '@udemy/resource-context-menu';
import {CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, useUDData} from '@udemy/ud-data';

import {CertificationTopicDiscoverySection} from './components/certification-topic-discovery-section.react-component';
import {TopicDiscoveryListContainerWrapper} from './components/topic-discovery-units-wrapper.react-component';
import {CERT_HERO_ENROLLMENT_THRESHOLD, SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS} from './constants';
import {TopicPageViewEvent} from './events';
import styles from './topic.module.less';
import {TopicProps} from './types/topic';
import {
    getLocalizedEnrollmentCount,
    getPageObjectIdFromTopic,
    getPageType,
    getPrimaryHeading,
} from './utils';

const RESOURCE_CONTEXT_MENU_PROPS = {context: CONTEXT_TYPES.ALL_COURSES};

export const CertificationTopic = (props: Omit<TopicProps, 'udData' | 'locale'>) => {
    const {
        certification,
        certDescription,
        topic,
        topicTitle,
        presetValue,
        numEnrollments,
        isConsumerSubsSubscriber,
        avgRating,
        numCourses,
        numExercises,
        questionsAndAnswers,
        showPersonalPlanBadge,
        showCodingExercisesBadge,
        certificationBundles,
    } = props;

    const udData = useUDData();
    const {locale, gettext, interpolate, ngettext} = useI18n();

    const mobileMaxMedia = useMatchMedia('mobile-max');
    const isMobile = mobileMaxMedia != null ? mobileMaxMedia : Boolean(udData.request?.isMobile);

    const pageObjectId = getPageObjectIdFromTopic(topic);

    useEffect(() => {
        Tracker.publishEvent(new TopicPageViewEvent({topicId: pageObjectId}));
    }, [pageObjectId]);

    const marketplaceCountryId = udData.Config.marketplace_country?.id;
    useEffect(() => {
        if (!topic.id || !marketplaceCountryId) {
            return;
        }
        const timeoutId = setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent(CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, {
                    detail: {
                        marketplace_country: {},
                        pageContext: {entityId: topic.id.toString()},
                    },
                }),
            );
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [topic.id, marketplaceCountryId]);

    const {voucherPool, examId, isAvailableForRegion} = useExamByTopicId(topic.id);
    const hasVoucher =
        !!voucherPool && !!examId && isAvailableForRegion && voucherPool.hasAvailableVouchers;

    const hasPracticeExam = (props.certificationBundles ?? []).some((bundle) =>
        (bundle.contents ?? []).some(
            (course) => (course as BrowseCourse).is_practice_test_course === true,
        ),
    );

    const certificationOccupations: CertificationOccupation[] = (
        topic.certificationOccupations ?? []
    )
        .filter((occupation): occupation is NonNullable<typeof occupation> => !!occupation?.id)
        .map((occupation) => ({
            id: occupation.id,
            name: occupation.name,
            indeedJobMarketInfo: {
                averageAnnualSalary: occupation.indeedJobMarketInfo?.averageAnnualSalary ?? null,
                openRoleCount: occupation.indeedJobMarketInfo?.openRoleCount ?? null,
                urlJobSearch: occupation.indeedJobMarketInfo?.urlJobSearch ?? '',
            },
        }));

    // Unreachable via TopicWrapper (the only caller, which already gates on certDescription), and by this
    // point the effects above — page-view, provider bridge, voucher query — have already run, so this is
    // NOT a substitute for that gate. It only prevents a broken render and narrows certDescription to a
    // string for the header below. Do not remove the TopicWrapper gate and rely on this.
    if (!certDescription) {
        return null;
    }

    const pageType = getPageType(udData, isConsumerSubsSubscriber);
    const primaryHeading = getPrimaryHeading({topic, topicTitle, presetValue, pageType});
    const certHeroNumLearners = getLocalizedEnrollmentCount(
        numEnrollments,
        locale,
        CERT_HERO_ENROLLMENT_THRESHOLD,
    );

    // `topic.images` is typed non-null but the schema documents it as null when a topic has no images
    // configured; now that every allowlisted topic reaches this line (not just badge topics), guard the
    // access. Legacy `certification` (from badgeClassesByTopic) is being retired as a render dependency and
    // can be undefined, so fall back to its image only when present.
    const topicHeroImage = topic.images?.heroImage;
    const topicBadgeImage = topic.images?.badgeImage;
    const heroImage = topicHeroImage
        ? {
              url: topicHeroImage.url,
              highDensityUrl: topicHeroImage.highDensityUrl,
          }
        : {url: certification?.image?.id ?? ''};
    const badgeImage = topicBadgeImage
        ? {
              url: topicBadgeImage.url,
              highDensityUrl: topicBadgeImage.highDensityUrl,
          }
        : undefined;

    const renderQuestionsAndAnswers = () => {
        if (!questionsAndAnswers || questionsAndAnswers.length === 0) {
            return null;
        }

        return (
            <div className={styles['section-parent-container']}>
                <h2 className={styles['faq-title']}>{gettext('Frequently asked questions')}</h2>
                <ContentArea className={classNames('ud-container', styles['area'])}>
                    <ContentGrid>
                        <ContentGridItem defaultColumn={'1 / span 4'} mdColumn={'2 / span 10'}>
                            <div className={styles['faq-container']}>
                                <QuestionsAndAnswers
                                    title={''}
                                    className={styles['faq-inner-container']}
                                    questionsAndAnswers={questionsAndAnswers}
                                    numRowsToShow={5}
                                    showMoreClass={styles['show-more-btn']}
                                    showMoreStyle={'link'}
                                    showMoreHasCount={true}
                                    showButtonIcon={true}
                                />
                            </div>
                        </ContentGridItem>
                    </ContentGrid>
                </ContentArea>
            </div>
        );
    };

    return (
        <>
            <div className="component-margin ud-container">
                <CertificationTopicHeader
                    name={primaryHeading}
                    topicDescription={certDescription}
                    images={{heroImage, badgeImage}}
                    stats={{
                        averageRating: avgRating,
                        numLearners: certHeroNumLearners,
                        numPracticeExercises: numExercises,
                        numCourses: numCourses,
                    }}
                    hasVoucher={hasVoucher}
                    hasPracticeExam={hasPracticeExam}
                />
            </div>
            <div className={'component-margin'}>
                <CertificationTopicStatsSection />
            </div>
            {!!certificationBundles[0]?.contents?.length && (
                <div className="component-margin ud-container">
                    <FunnelLogContextProvider pageType="topic">
                        <CertificationVoucherBundleUnitCarousel
                            title={gettext('Get Certified with Top Instructors')}
                            subtitle={gettext(
                                'Explore curated bundles from expert instructors. Browse to compare teaching styles and prep strategies.',
                            )}
                            units={certificationBundles.map((bundle) => ({
                                bundle: bundle,
                                showCommonInstructor: true,
                                ...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
                            }))}
                        />
                    </FunnelLogContextProvider>
                </div>
            )}
            <div className="component-margin ud-container">
                <CertificationTopicDiscoverySection
                    topic={topic}
                    enrollmentStat={props.enrollmentStat}
                    isMobile={isMobile}
                    pageType={pageType}
                    isConsumerSubsSubscriber={isConsumerSubsSubscriber}
                    isSubsLoading={props.isSubsLoading}
                    showPersonalPlanBadge={props.showPersonalPlanBadge}
                    useVerticalRecoCourseCardStyle={props.useVerticalRecoCourseCardStyle}
                    threeCardVerticalLayoutExperimentIsEnabled={
                        props.threeCardVerticalLayoutExperimentIsEnabled
                    }
                    isTopicVoucherModuleAboveBundle={props.isTopicVoucherModuleAboveBundle}
                    certificationOccupations={certificationOccupations}
                    certificationName={topic.name}
                />
            </div>
            <div className="component-margin ud-container">
                <Provider
                    isConsumerSubsSubscriber={isConsumerSubsSubscriber}
                    showPersonalPlanBadge={showPersonalPlanBadge}
                    showCodingExercisesBadge={showCodingExercisesBadge}
                >
                    <ResourceContextMenuProvider
                        resourceContextMenuProps={RESOURCE_CONTEXT_MENU_PROPS}
                    >
                        <TopicDiscoveryListContainerWrapper
                            {...props}
                            udData={udData}
                            locale={locale}
                            interpolate={interpolate}
                            ngettext={ngettext}
                            gettext={gettext}
                            isMobile={isMobile}
                            pageType={pageType}
                        />
                    </ResourceContextMenuProvider>
                </Provider>
            </div>
            {renderQuestionsAndAnswers()}
        </>
    );
};
